/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { isEqual } from 'lodash'

import type { EuiBasicTableColumn } from '@elastic/eui'
import {
  EuiButtonIcon,
  EuiCopy,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiBasicTable,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { messages } from '../messages'
import { stringifyTag } from '../helpers'
import TagBadge from '../TagBadge'

import type { Tag } from '../types'
import type { ValidationErrors } from './AddTagsForm'
import type { ReactElement } from 'react'

type Props = {
  tags: Tag[]
  onRemoveTag: (tag: Tag) => void
  onUpdateTag: (oldTag: Tag, newTag: Tag) => void
  validateTag: (currentTag: Tag, currentTags: Tag[]) => ValidationErrors
  isAddingNewTag: boolean
  setIsAddingNewTag: (isAddingNewTag: boolean) => void
}

const TagsTable = ({
  tags,
  onRemoveTag,
  onUpdateTag,
  validateTag,
  isAddingNewTag,
  setIsAddingNewTag,
}: Props) => {
  const { formatMessage } = useIntl()
  const [editingTagId, setEditingTagId] = useState<string | null>(null)
  const [editKey, setEditKey] = useState('')
  const [editValue, setEditValue] = useState('')
  const [errors, setErrors] = useState<ValidationErrors>({})

  const editKeyRef = useRef<HTMLInputElement>(null)
  const editValueRef = useRef<HTMLInputElement>(null)

  const getTagId = (tag: Tag): string => `${tag.key}-${tag.value}`

  const getTagValidationError = (): ReactElement<typeof FormattedMessage> | null => {
    const [error = null] = Object.values(errors)
    return error
  }

  const startEditing = (tag: Tag) => {
    setIsAddingNewTag(false)
    setEditingTagId(getTagId(tag))
    setEditKey(tag.key)
    setEditValue(tag.value)
    setErrors({})
    setTimeout(() => editKeyRef.current?.focus(), 0)
  }

  const cancelEditing = () => {
    setEditingTagId(null)
    setEditKey('')
    setEditValue('')
    setErrors({})
  }

  const saveEdit = (oldTag: Tag) => {
    const newTag: Tag = { key: editKey.trim(), value: editValue.trim() }
    const validationErrors = validateTag(
      newTag,
      tags.filter((t) => !isEqual(t, oldTag)),
    )
    setErrors(validationErrors)

    const error = getTagValidationError()

    if (error === null && editKey.trim() !== '' && editValue.trim() !== '') {
      onUpdateTag(oldTag, newTag)
      cancelEditing()
    }
  }

  const onEditKeyChange = (e: React.ChangeEvent<HTMLInputElement>, oldTag: Tag) => {
    const inputFieldText = e.target.value
    setEditKey(inputFieldText)
    const otherTags = tags.filter((t) => !isEqual(t, oldTag))

    setErrors(validateTag({ key: inputFieldText, value: editValue }, otherTags))
  }

  const onEditValueChange = (e: React.ChangeEvent<HTMLInputElement>, oldTag: Tag) => {
    const inputFieldText = e.target.value
    setEditValue(inputFieldText)
    const otherTags = tags.filter((t) => !isEqual(t, oldTag))

    setErrors(validateTag({ key: editKey, value: inputFieldText }, otherTags))
  }

  const canSaveTag = (): boolean =>
    error === null && editKey.trim() !== '' && editValue.trim() !== ''

  const onEscape = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  const onKeyDownOnKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      editValueRef.current?.focus()
    }

    onEscape(e)
  }

  const onKeyDownOnValue = (e: React.KeyboardEvent, oldTag: Tag) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      if (canSaveTag()) {
        saveEdit(oldTag)
      }
    }

    onEscape(e)
  }

  const error = getTagValidationError()

  const columns: Array<EuiBasicTableColumn<Tag>> = [
    {
      name: <FormattedMessage id='resource-tags.flyout.table-tags-column' defaultMessage='Tags' />,
      render: (tag: Tag) => {
        const tagId = getTagId(tag)
        const isEditing = editingTagId === tagId

        if (isEditing && !isAddingNewTag) {
          return (
            <EuiForm>
              <EuiFormRow
                isInvalid={!!error}
                error={<div style={{ maxWidth: '70%' }}>{error}</div>}
                fullWidth={true}
              >
                <EuiFlexGroup alignItems='center' justifyContent='spaceBetween' gutterSize='m'>
                  <EuiFlexItem grow={4}>
                    <EuiFieldText
                      compressed={true}
                      inputRef={editKeyRef}
                      autoFocus={true}
                      isInvalid={!!errors.key_max_length || !!errors.invalid_key}
                      placeholder={formatMessage({
                        id: 'resource-tags.key-placeholder',
                        defaultMessage: 'Key',
                      })}
                      onChange={(e) => onEditKeyChange(e, tag)}
                      onKeyDown={onKeyDownOnKey}
                      value={editKey}
                    />
                  </EuiFlexItem>

                  <EuiFlexItem grow={4}>
                    <EuiFieldText
                      compressed={true}
                      inputRef={editValueRef}
                      isInvalid={!!errors.value_max_length || !!errors.invalid_value}
                      placeholder={formatMessage({
                        id: 'resource-tags.value-placeholder',
                        defaultMessage: 'Value',
                      })}
                      onChange={(e) => onEditValueChange(e, tag)}
                      onKeyDown={(e) => onKeyDownOnValue(e, tag)}
                      value={editValue}
                    />
                  </EuiFlexItem>

                  <EuiFlexItem grow={false}>
                    <EuiFlexGroup gutterSize='s' alignItems='center'>
                      <EuiFlexItem grow={false}>
                        <EuiButtonIcon
                          size='s'
                          display='base'
                          isDisabled={!canSaveTag()}
                          onClick={() => saveEdit(tag)}
                          iconType='check'
                          aria-label={formatMessage({
                            id: 'resource-tags.flyout.save-tag-button',
                            defaultMessage: 'Save',
                          })}
                          color='success'
                        />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButtonIcon
                          size='s'
                          display='base'
                          onClick={cancelEditing}
                          iconType='cross'
                          aria-label={formatMessage({
                            id: 'resource-tags.flyout.cancel-edit-button',
                            defaultMessage: 'Cancel',
                          })}
                          color='danger'
                        />
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFormRow>
            </EuiForm>
          )
        }

        return (
          <EuiFlexGroup justifyContent='spaceBetween'>
            <EuiFlexItem grow={false}>
              <TagBadge tag={tag} hasCopyButton={false} />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup gutterSize='s'>
                <EuiButtonIcon
                  iconType='pencil'
                  aria-label={formatMessage(messages.editTag)}
                  onClick={() => startEditing(tag)}
                />
                <EuiCopy textToCopy={stringifyTag(tag)}>
                  {(copy) => (
                    <EuiButtonIcon
                      iconType='copyClipboard'
                      onClick={copy}
                      aria-label={formatMessage(messages.copyClipboardTag)}
                    />
                  )}
                </EuiCopy>
                <EuiButtonIcon
                  iconType='trash'
                  color='danger'
                  aria-label={formatMessage(messages.removeTag)}
                  onClick={() => onRemoveTag(tag)}
                />
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        )
      },
      width: '190px',
    },
  ]
  return (
    <EuiBasicTable<Tag>
      tableCaption={formatMessage({
        id: 'resource-tags.flyout.table-caption',
        defaultMessage: 'List of tags. Use the action buttons to edit, copy, or remove tags.',
      })}
      data-test-id='resource-tags.flyout.table'
      items={tags}
      columns={columns}
    />
  )
}

export default TagsTable
